import { useState, useCallback, useRef, useEffect } from 'react';
import api from '@/lib/api';

export type IconGeneratorStatus =
  | 'idle'
  | 'generating'
  | 'generated'
  | 'saving'
  | 'saved'
  | 'error';

interface IconGeneratorState {
  status: IconGeneratorStatus;
  previewUrl: string | null;
  requestId: string | null;
  /** Vault document id, populated once the poll returns 'complete'.
   * The /save endpoint keys off this, not requestId, because the server
   * resolves the doc from the durable vault row — not from in-memory
   * per-replica state. */
  documentId: string | null;
  fileId: string | null;
  attachedIconKey: string | null;
  error: string | null;
}

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 90; // 3 minutes max

export function useIconGenerator() {
  const [state, setState] = useState<IconGeneratorState>({
    status: 'idle',
    previewUrl: null,
    requestId: null,
    documentId: null,
    fileId: null,
    attachedIconKey: null,
    error: null,
  });

  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  // Clean up polling on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const pollStatus = useCallback(async (requestId: string, attempt: number = 0) => {
    if (attempt >= MAX_POLL_ATTEMPTS) {
      setState((prev) => ({
        ...prev,
        status: 'error',
        error: 'Icon generation timed out. Please try again.',
      }));
      return;
    }

    try {
      const result = await api.getIconGeneratorStatus(requestId);

      if (result.status === 'complete') {
        setState((prev) => ({
          ...prev,
          status: 'generated',
          previewUrl: result.previewUrl || null,
          documentId: result.documentId || null,
          requestId,
        }));
        return;
      }

      if (result.status === 'error') {
        setState((prev) => ({
          ...prev,
          status: 'error',
          error: result.error || 'Icon generation failed',
        }));
        return;
      }

      // Still pending — poll again
      pollingRef.current = setTimeout(() => pollStatus(requestId, attempt + 1), POLL_INTERVAL_MS);
    } catch (err) {
      setState((prev) => ({
        ...prev,
        status: 'error',
        error: err instanceof Error ? err.message : 'Failed to check status',
      }));
    }
  }, []);

  const generate = useCallback(
    async (name: string, description: string, useDefaultStyle: boolean, style?: string) => {
      stopPolling();
      setState({
        status: 'generating',
        previewUrl: null,
        requestId: null,
        documentId: null,
        fileId: null,
        attachedIconKey: null,
        error: null,
      });

      try {
        const result = await api.generateIcon({
          name,
          description,
          useDefaultStyle,
          style: useDefaultStyle ? undefined : style,
        });

        setState((prev) => ({ ...prev, requestId: result.requestId }));
        pollStatus(result.requestId);
      } catch (err) {
        setState((prev) => ({
          ...prev,
          status: 'error',
          error: err instanceof Error ? err.message : 'Failed to start generation',
        }));
      }
    },
    [pollStatus, stopPolling],
  );

  const save = useCallback(
    async (options?: { fileName?: string; attachToArcheId?: string }) => {
      // documentId is populated by the poll once status hits 'complete';
      // it's the vault id the server resolves on /save. Without it the
      // user couldn't have reached the "save" UI affordance, but guard
      // for completeness.
      if (!state.documentId) return;

      setState((prev) => ({ ...prev, status: 'saving' }));

      try {
        const result = await api.saveIcon({
          documentId: state.documentId,
          fileName: options?.fileName,
          attachToArcheId: options?.attachToArcheId,
        });

        setState((prev) => ({
          ...prev,
          status: 'saved',
          fileId: result.fileId,
          attachedIconKey: result.attachedIconKey,
        }));
      } catch (err) {
        setState((prev) => ({
          ...prev,
          status: 'error',
          error: err instanceof Error ? err.message : 'Failed to save icon',
        }));
      }
    },
    [state.documentId],
  );

  const reset = useCallback(() => {
    stopPolling();
    setState({
      status: 'idle',
      previewUrl: null,
      requestId: null,
      documentId: null,
      fileId: null,
      attachedIconKey: null,
      error: null,
    });
  }, [stopPolling]);

  return {
    ...state,
    generate,
    save,
    reset,
  };
}
